import { useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { CheckCircle, XCircle } from "react-bootstrap-icons";
import { SubmitButton, CancelButton } from "../../shared";

/**
 * Confirmation modal for approving or declining the entire payout run.
 * `pendingCount` triggers the bulk-decision prompt when > 0.
 */
export function RunDecisionModal({
  action,
  run,
  pendingCount,
  onCancel,
  onConfirm,
  isSubmitting = false,
}) {
  const isApprove = action === "approve";
  const [reason, setReason] = useState("");
  const [removeContractsToPoolOnDecline, setRemoveContractsToPoolOnDecline] =
    useState(true);

  const reasonRequired = !isApprove;
  const valid = reasonRequired ? reason.trim().length > 0 : true;

  return (
    <Modal show onHide={onCancel} centered className="commission-modal-scope">
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">
          {isApprove ? "Approve payout run" : "Decline payout run"} — {run.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-secondary small mb-3">
          {isApprove
            ? "Once approved, the run is locked. Rows can no longer be adjusted, declined, or removed."
            : "Declining the run locks it as declined. No lines will be paid."}
        </p>

        {pendingCount > 0 && (
          <div
            className={`pending-decision-inline mb-3 small ${
              isApprove
                ? "pending-decision-inline--approve"
                : "pending-decision-inline--decline"
            }`}
          >
            {isApprove ? (
              <>
                {pendingCount} pending record{pendingCount === 1 ? "" : "s"}{" "}
                will be approved automatically after you click "Approve run".
              </>
            ) : (
              <>
                All records will be declined automatically after you click
                "Decline run".
              </>
            )}
          </div>
        )}

        <Form.Group className="mt-3">
          <Form.Label className="field-label">
            {isApprove ? "Notes (optional)" : "Reason"}
            {reasonRequired && <span className="text-danger"> *</span>}
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={
              isApprove
                ? "Any notes to save with this approval?"
                : "Why is this run being declined?"
            }
            autoFocus
          />
          <Form.Text className="char-counter">{reason.length}/500</Form.Text>
        </Form.Group>

        {!isApprove && (
          <Form.Group className="mt-3">
            <Form.Check
              type="checkbox"
              id="remove-contracts-to-pool"
              checked={removeContractsToPoolOnDecline}
              onChange={(e) =>
                setRemoveContractsToPoolOnDecline(e.target.checked)
              }
              label="Return all records in this run back to the pool"
            />
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <CancelButton onClick={onCancel} disabled={isSubmitting} />
        <SubmitButton
          text={isApprove ? "Approve run" : "Decline run"}
          color={isApprove ? "green" : "red"}
          disabled={!valid}
          isSubmitting={isSubmitting}
          onClick={() =>
            onConfirm({
              action,
              reason: reason.trim() || null,
              removeContractsToPoolOnDecline,
            })
          }
          icon={isApprove ? <CheckCircle size={14} /> : <XCircle size={14} />}
        />
      </Modal.Footer>
    </Modal>
  );
}
